import { useState } from 'react';
import type { GeneratedNoc, NocStatus, NotarizedNocResult } from '../../shared/api';
import { Banner, Button, ChoiceCard, Field, FilePicker, Input, KeyValue, Pill, Steps, Textarea } from '../../components/ui';
import { api, ApiError, fileUrl } from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { NOC_STATUS_LABELS, NOC_STATUS_TONE } from '../../lib/selfService';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';

/**
 * Self-service notarization of the Notice of Commencement.
 *
 * Prime-only: F.S. 713.13 puts the NOC on the owner's side of the contract, and
 * the backend refuses a gc/sub job outright, so the tab says so rather than
 * offering a form that will 403.
 *
 * The step comes from nocStatus, which the backend derives from the job row —
 * so this agrees with the badge in the header and survives a reload.
 */
const STEP_LABELS = ['Choose method', 'Generate NOC', 'Notarize', 'Record'];

const STEP_BY_STATUS = {
  not_started: 0,
  awaiting_notarization: 2,
  notarized: 3,
  recorded: 3,
} as const;

export function NocStatusBadge({ status }: { status: NocStatus }) {
  return <Pill tone={NOC_STATUS_TONE[status]}>{NOC_STATUS_LABELS[status]}</Pill>;
}

export default function NocTab({ job, reload }: TabProps) {
  const { can } = useSession();
  const mayAct = can('manager');
  const isPrime = job.contractorRole === 'prime';

  const [chose, setChose] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generated, setGenerated] = useState<GeneratedNoc | null>(null);
  const [uploaded, setUploaded] = useState<NotarizedNocResult | null>(null);

  const [form, setForm] = useState({ permitNo: '', description: '', expirationDate: '' });
  const [lender, setLender] = useState({ name: '', address: '', phone: '' });
  const [surety, setSurety] = useState({ name: '', address: '', phone: '', bondAmount: '' });
  const [file, setFile] = useState<File | null>(null);
  const [notary, setNotary] = useState({ name: '', commission: '', notes: '' });
  const [bookPage, setBookPage] = useState('');

  if (!isPrime) {
    return (
      <Banner tone="watch" title="Notice of Commencement is prime-only">
        A Notice of Commencement is recorded by the party in privity with the owner (F.S. 713.13). This job is filed as{' '}
        {job.contractorRole === 'gc' ? 'a general contractor' : 'a subcontractor'}, so there is nothing to record here.
        The NOC tab on the prime's job is where this happens.
      </Banner>
    );
  }
  // No fetch: getJobDetail carries nocStatus, nocProof and erecording, so the
  // page draws from one response. GET /noc/status still exists for a targeted
  // refresh, but reload() re-reads the detail and is what runs after a write.
  const status = job.nocStatus ?? 'not_started';
  const derived = STEP_BY_STATUS[status];
  const step = derived === 0 && chose ? 1 : derived;
  const unsignedUrl = fileUrl(generated?.pdfUrl ?? job.nocProof?.unsignedPdfUrl ?? job.nocSelfServiceUrl ?? null);
  const notarizedUrl = fileUrl(uploaded?.proof.notarizedPdfUrl ?? job.nocProof?.notarizedPdfUrl ?? null);
  const erecording = uploaded?.erecording ?? job.erecording ?? null;
  const notaryOnFile = job.nocProof;

  const after = async () => {
    await reload();
  };

  const generate = async () => {
    setError(null);
    setBusy(true);
    try {
      const res = await api.post<GeneratedNoc>(`/api/jobs/${job.id}/noc/self-service`, {
        permitNo: form.permitNo.trim() || undefined,
        generalDescriptionOfImprovement: form.description,
        expirationDate: form.expirationDate || undefined,
        // Sent only when actually filled in; a half-empty party fails validation.
        lender: lender.name.trim() && lender.address.trim() ? { ...lender, phone: lender.phone || null } : undefined,
        surety:
          surety.name.trim() && surety.address.trim() && surety.bondAmount.trim()
            ? { ...surety, phone: surety.phone || null }
            : undefined,
      });
      setGenerated(res);
      await after();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not generate the Notice of Commencement');
    } finally {
      setBusy(false);
    }
  };

  const uploadNotarized = async () => {
    setError(null);
    if (!file) return setError('Choose the notarized PDF first.');
    setBusy(true);
    try {
      const body = new FormData();
      if (notary.name.trim()) body.append('notaryName', notary.name.trim());
      if (notary.commission.trim()) body.append('notaryCommission', notary.commission.trim());
      if (notary.notes.trim()) body.append('notes', notary.notes.trim());
      body.append('file', file);
      const res = await api.post<NotarizedNocResult>(`/api/jobs/${job.id}/noc/notarized`, body);
      setUploaded(res);
      setFile(null);
      await after();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not upload the notarized NOC');
    } finally {
      setBusy(false);
    }
  };

  const record = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/jobs/${job.id}/noc/recorded`, { bookPage });
      setBookPage('');
      await after();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not record the NOC');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Steps steps={STEP_LABELS} current={step} />
        <NocStatusBadge status={status} />
      </div>

      {!mayAct && (
        <Banner tone="watch" title="Your seat cannot handle the NOC">
          Generating or filing a Notice of Commencement needs a manager or admin seat.
        </Banner>
      )}
      {error && <Banner tone="urgent">{error}</Banner>}

      {step === 0 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">How should the NOC be notarized?</h3>
          <div className="flex flex-col gap-3 sm:flex-row">
            <ChoiceCard
              title="Online notarization (RON)"
              badge={<Pill>Coming soon</Pill>}
              disabled
              description="The owner signs with a remote online notary; we collect the executed document for you."
            />
            <ChoiceCard
              title="Self-service notarization"
              description="Download the NOC, take it to your local notary, then upload the notarized document."
              onSelect={() => setChose(true)}
              disabled={!mayAct}
            />
          </div>
        </section>
      )}

      {step === 1 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">Generate the Notice of Commencement</h3>
          <Field label="General description of improvement" hint="Required. Printed on the notice, per F.S. 713.13(1)(a)2.">
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Re-roof main residence"
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Permit number (optional)">
              <Input value={form.permitNo} onChange={(e) => setForm({ ...form, permitNo: e.target.value })} />
            </Field>
            <Field label="Expiration date (optional)" hint="Defaults to one year from recording if left blank.">
              <Input type="date" value={form.expirationDate} onChange={(e) => setForm({ ...form, expirationDate: e.target.value })} />
            </Field>
          </div>

          <details className="rounded-lg border border-border bg-surface-alt/40 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-ink">Lender (optional)</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <Field label="Name"><Input value={lender.name} onChange={(e) => setLender({ ...lender, name: e.target.value })} /></Field>
              <Field label="Address"><Input value={lender.address} onChange={(e) => setLender({ ...lender, address: e.target.value })} /></Field>
              <Field label="Phone"><Input value={lender.phone} onChange={(e) => setLender({ ...lender, phone: e.target.value })} /></Field>
            </div>
          </details>

          <details className="rounded-lg border border-border bg-surface-alt/40 p-4">
            <summary className="cursor-pointer text-sm font-semibold text-ink">Surety (optional)</summary>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <Field label="Name"><Input value={surety.name} onChange={(e) => setSurety({ ...surety, name: e.target.value })} /></Field>
              <Field label="Address"><Input value={surety.address} onChange={(e) => setSurety({ ...surety, address: e.target.value })} /></Field>
              <Field label="Phone"><Input value={surety.phone} onChange={(e) => setSurety({ ...surety, phone: e.target.value })} /></Field>
              <Field label="Bond amount"><Input value={surety.bondAmount} onChange={(e) => setSurety({ ...surety, bondAmount: e.target.value })} /></Field>
            </div>
          </details>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
            <Button variant="ghost" onClick={() => setChose(false)} disabled={busy}>
              ← Back
            </Button>
            <Button onClick={generate} disabled={busy || form.description.trim().length < 3}>
              {busy ? 'Generating…' : 'Generate NOC'}
            </Button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">Take it to a notary</h3>
          <Banner tone="watch" title="Not complete until the executed document is back">
            Download and print this NOC. Take it to a notary public for notarization — F.S. 713.13(1)(g) requires the
            signature to be notarized. Then upload the executed document here.
          </Banner>
          {unsignedUrl && (
            <a href={unsignedUrl} target="_blank" rel="noreferrer" className="inline-block text-sm font-semibold text-tint hover:underline">
              Download the unsigned NOC
            </a>
          )}
          {mayAct && (
            <div className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
              <FilePicker
                label="Notarized NOC"
                accept="application/pdf,image/*"
                file={file}
                onPick={setFile}
                hint="The executed document, as the notary handed it back."
                disabled={busy}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Notary name (optional)">
                  <Input value={notary.name} onChange={(e) => setNotary({ ...notary, name: e.target.value })} />
                </Field>
                <Field label="Commission number (optional)">
                  <Input value={notary.commission} onChange={(e) => setNotary({ ...notary, commission: e.target.value })} />
                </Field>
              </div>
              <Field label="Notes (optional)">
                <Input value={notary.notes} onChange={(e) => setNotary({ ...notary, notes: e.target.value })} />
              </Field>
              <Button onClick={uploadNotarized} disabled={busy || !file}>
                {busy ? 'Uploading…' : 'Upload Notarized NOC'}
              </Button>
            </div>
          )}
        </section>
      )}

      {step === 3 && (
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">Record it at the clerk</h3>
          {status === 'recorded' ? (
            <Banner tone="ok" title="Notice of Commencement recorded">
              Book/page {job.nocRecordedBookPage}.
            </Banner>
          ) : (
            <Banner tone="info" title="Notarized — not yet recorded">
              Submit the notarized NOC for recording at the county clerk's office. Once recorded, enter the Book and
              Page number below.
            </Banner>
          )}

          <div className="flex flex-wrap gap-4">
            {notarizedUrl && (
              <a href={notarizedUrl} target="_blank" rel="noreferrer" className="text-sm font-semibold text-tint hover:underline">
                Download the notarized NOC
              </a>
            )}
            {unsignedUrl && (
              <a href={unsignedUrl} target="_blank" rel="noreferrer" className="text-sm text-muted hover:underline">
                Download the unsigned original
              </a>
            )}
          </div>

          {notaryOnFile && (
            <KeyValue
              rows={[
                ['Notary', notaryOnFile.notaryName],
                ['Commission', notaryOnFile.notaryCommission],
                ['Uploaded', fmtDate(notaryOnFile.uploadedAt)],
              ]}
            />
          )}

          {erecording && (
            <div className="rounded-lg border border-border bg-surface-alt/40 p-4 text-sm">
              <p className="font-semibold text-ink">County e-recording</p>
              <p className="mt-1 text-muted">{erecording.instructions}</p>
              <a className="mt-2 inline-block text-tint hover:underline" href={erecording.portalUrl} target="_blank" rel="noreferrer">
                {erecording.portalUrl}
              </a>
            </div>
          )}

          {status !== 'recorded' && mayAct && (
            <form onSubmit={record} className="space-y-4 rounded-lg border border-border bg-surface-alt/40 p-4">
              <Field label="Book / page" hint="As stamped by the clerk, e.g. 35100/42.">
                <Input required value={bookPage} onChange={(e) => setBookPage(e.target.value)} />
              </Field>
              <Button type="submit" disabled={busy || !bookPage.trim()}>
                {busy ? 'Saving…' : 'Record NOC'}
              </Button>
            </form>
          )}
        </section>
      )}
    </div>
  );
}
